/* ===================================================================
   ClientPhotoUpload - the ONE component for setting a client's review-request
   photo (Steve, 2026-07-29). Each client uploads ONE photo of themselves - with
   their dog, their team, or their work vehicle - and it rides at the top of
   every review-request email. A face lifts response rate.

   Lives GLOBALLY IN-APP (Settings > Business profile is the primary home) AND in
   onboarding intake. Same component in both, plus the Requests preview shows the
   result. Wired to the real endpoints:
     POST   /api/v1/business/photo   (JSON image_base64) -> {photo_url,...}
     DELETE /api/v1/business/photo   -> {ok, photo_url:null}

   HONEST STATES (older-tradesman-on-a-glary-phone UX):
     - no photo   -> a BIG tap target: "Take a photo or choose one" (accept
                     image/* with no `capture` attr, so mobile offers Camera OR
                     Library). High contrast, bold.
     - selected   -> instant local preview while it uploads.
     - set        -> the photo + clear "Replace" / "Remove".
     - 503        -> migration #36 not applied yet: a CALM "photo uploads are
                     being switched on, check back shortly" - never a scary error
                     or a crash. The moment #36 applies it just works.
     - error      -> honest, specific, plain (too large / not an image / retry).

   No dev artifacts: no raw URLs, JSON, keys, or place ids are ever shown.
   =================================================================== */
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type BusinessProfile } from '../lib/api';
import { useBusiness } from '../hooks';
import { qk } from '../hooks/queryKeys';
import '../styles/clientphoto.css';

/* 8 MB matches the endpoint's own cap; we reject bigger BEFORE reading so a
   giant file never hangs the tab. */
const MAX_BYTES = 8 * 1024 * 1024;

type UiState =
  | { kind: 'ready' }
  | { kind: 'uploading' }
  | { kind: 'unavailable' } // 503: storage columns not live yet (migration #36)
  | { kind: 'error'; msg: string };

export function ClientPhotoUpload({
  variant = 'panel',
}: {
  variant?: 'panel' | 'compact';
}) {
  const qc = useQueryClient();
  const business = useBusiness();
  const fileRef = useRef<HTMLInputElement>(null);

  // The photo the SERVER knows about. The business-profile GET may not surface
  // request_photo_url on every build; absent -> no photo shown on load (honest).
  const serverPhoto =
    (business.data?.profile as BusinessProfile | undefined)?.request_photo_url ?? null;
  // A successful upload/removal THIS session overrides the server value so the
  // owner sees their change immediately even if the GET doesn't echo it back.
  const [sessionPhoto, setSessionPhoto] = useState<string | null | undefined>(undefined);
  // The just-picked file, shown instantly (before/independent of the server).
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [ui, setUi] = useState<UiState>({ kind: 'ready' });

  const photo = sessionPhoto !== undefined ? sessionPhoto : serverPhoto;
  const shown = localPreview ?? photo;

  const upload = useMutation({
    mutationFn: (dataUrl: string) => api.setBusinessPhoto(dataUrl),
    onMutate: () => setUi({ kind: 'uploading' }),
    onSuccess: (res) => {
      setSessionPhoto(res.photo_url);
      setLocalPreview(null);
      setUi({ kind: 'ready' });
      void qc.invalidateQueries({ queryKey: qk.business() });
    },
    onError: (e: unknown) => {
      // "Not available yet" covers every pre-launch backend state gracefully:
      //   503 photo_storage_unavailable -> migration #36 not applied,
      //   404/405/501               -> the photo route isn't in the API build yet,
      //   0 network_error           -> the not-ready backend is cross-origin and
      //                                its error responses aren't CORS-readable,
      //                                so the browser surfaces the failure as a
      //                                network error rather than a clean 503.
      // In every case the owner did nothing wrong, so we show the calm "being
      // switched on" state (keeping their picked preview), never a scary error.
      // The common client-side mistakes (too large / not an image) are caught
      // BEFORE any request in onFile, so they still get their specific message.
      const notReady =
        e instanceof ApiError &&
        (e.status === 503 ||
          e.status === 404 ||
          e.status === 405 ||
          e.status === 501 ||
          e.status === 0 ||
          e.message === 'photo_storage_unavailable' ||
          e.message === 'network_error');
      if (notReady) {
        setUi({ kind: 'unavailable' });
      } else if (e instanceof ApiError && e.status === 413) {
        setLocalPreview(null);
        setUi({ kind: 'error', msg: 'That photo is a bit large. Please choose one under 8 MB.' });
      } else if (e instanceof ApiError && e.status === 400) {
        setLocalPreview(null);
        setUi({ kind: 'error', msg: "That file isn't a photo we can use. Try a JPG or PNG." });
      } else {
        setUi({ kind: 'error', msg: "We couldn't upload that just now. Please try again." });
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteBusinessPhoto(),
    onSuccess: () => {
      setSessionPhoto(null);
      setLocalPreview(null);
      setUi({ kind: 'ready' });
      void qc.invalidateQueries({ queryKey: qk.business() });
    },
    onError: () => setUi({ kind: 'error', msg: "We couldn't remove that just now. Please try again." }),
  });

  function pick() {
    fileRef.current?.click();
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUi({ kind: 'error', msg: "That file isn't a photo. Please choose a JPG or PNG." });
      return;
    }
    if (file.size > MAX_BYTES) {
      setUi({ kind: 'error', msg: 'That photo is a bit large. Please choose one under 8 MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setLocalPreview(dataUrl);
      setUi({ kind: 'ready' });
      upload.mutate(dataUrl);
    };
    reader.onerror = () =>
      setUi({ kind: 'error', msg: "We couldn't read that file. Please try another photo." });
    reader.readAsDataURL(file);
  }

  const uploading = ui.kind === 'uploading' || upload.isPending;
  const busy = uploading || remove.isPending;

  return (
    <section className={'cph' + (variant === 'compact' ? ' cph--compact' : '')}>
      <div className="cph__head">
        <span className="cph__title">Your photo on every review request</span>
        <span className="cph__sub">
          Add one photo of you, your team, or your work vehicle. It rides at the
          top of every review request you send. A friendly face gets more replies.
        </span>
      </div>

      <input
        ref={fileRef}
        className="cph__file"
        type="file"
        accept="image/*"
        onChange={onFile}
        aria-hidden="true"
        tabIndex={-1}
      />

      {shown ? (
        <div className="cph__has">
          <div className="cph__imgwrap">
            <img className="cph__img" src={shown} alt="Your review-request photo" />
            {uploading && (
              <div className="cph__overlay" role="status">
                <span className="cph__spinner" aria-hidden="true" />
                Uploading...
              </div>
            )}
          </div>
          <div className="cph__actions">
            <button type="button" className="cph__btn cph__btn--primary" onClick={pick} disabled={busy}>
              Replace photo
            </button>
            <button
              type="button"
              className="cph__btn cph__btn--ghost"
              onClick={() => remove.mutate()}
              disabled={busy}
            >
              {remove.isPending ? 'Removing...' : 'Remove photo'}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="cph__drop" onClick={pick} disabled={busy}>
          <span className="cph__dropico" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
              <rect x="3" y="5" width="18" height="14" rx="3" />
              <circle cx="12" cy="12" r="3.2" />
              <path d="M8 5l1.4-2h5.2L16 5" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="cph__droplab">Take a photo or choose one</span>
          <span className="cph__dropsub">Tap to use your camera or pick from your photos</span>
        </button>
      )}

      {ui.kind === 'unavailable' && (
        <div className="cph__note cph__note--calm" role="status">
          <strong>Photo uploads are being switched on.</strong> Check back shortly
          {shown ? ' - we\'ll save this photo then.' : '.'} Nothing is wrong on your end.
        </div>
      )}
      {ui.kind === 'error' && (
        <div className="cph__note cph__note--err" role="alert">
          {ui.msg}
        </div>
      )}
    </section>
  );
}

export default ClientPhotoUpload;
